<template>
  <SettingsLayout class="bg-white dark:bg-slate-900">
    <template #body>
      <div class="settings-content bg-white dark:bg-slate-900 px-12">
        <!-- 筛选区域 -->
        <div class="filter-section">
          <div class="space-y-4">
            <!-- 筛选条件 -->
            <div class="flex items-end">
              <!-- IP 地址输入 -->
              <div class="w-[200px]">
                <label class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
                  {{ $t('BLACKLIST.IP_BLACKLIST.IP_ADDRESS') }}
                </label>
                <div class="flex h-10 items-center border border-slate-200 dark:border-slate-600 rounded-md bg-white dark:bg-slate-900 hover:border-indigo-500 focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500 px-3 ip-input-group">
                  <template v-for="(segment, index) in ipSegments" :key="index">
                    <input
                      v-model="ipSegments[index]"
                      type="text"
                      maxlength="3"
                      class="reset-base flex-1 min-w-0 text-center bg-transparent text-sm text-slate-900 dark:text-slate-100"
                      style="-webkit-appearance: none; appearance: none; border: none; outline: none;"
                      @input="handleIpInput($event, index)"
                      @keydown.backspace="handleBackspace($event, index)"
                      @paste="handlePaste"
                      :data-index="index"
                    >
                    <span 
                      v-if="index < 3" 
                      class="text-slate-600 mx-1 select-none shrink-0"
                    >.</span>
                  </template>
                </div>
              </div>
              <!-- 操作按钮 -->
              <div class="flex space-x-2 ml-8">
                <WootButton variant="primary" @click="onSearch">
                  {{ $t('BLACKLIST.FILTERS.SEARCH_BUTTON') }}
                </WootButton>
                <WootButton variant="clear" @click="onReset">
                  {{ $t('BLACKLIST.FILTERS.RESET_BUTTON') }}
                </WootButton>
              </div>
            </div>
          </div>
        </div>
        <!-- 添加IP范围 -->
        <div class="mb-4">
          <span 
            class="text-[#2781F6] cursor-pointer hover:text-[#1a6ad8] text-sm font-medium"
            @click="openAddIpRangeModal"
          >
            {{ $t('BLACKLIST.IP_BLACKLIST.ADD_IP_RANGE') }}
          </span>
        </div>
        
        <!-- IP段添加弹框 -->
        <Modal
          v-model:show="showAddIpRangeModal"
          :on-close="() => showAddIpRangeModal = false"
        >
          <AddIpRange 
            @close="() => {
              showAddIpRangeModal = false;
              isEditMode = false;
              editingBlacklistData = null;
            }" 
            @refresh="fetchIPBlacklistRecords"
            :editing-data="editingBlacklistData"
            :is-edit-mode="isEditMode"
          />
        </Modal>
        
        <!-- 列表区域 -->
        <div class="list-section">
          <IPBlacklistTable 
            :blacklist="ipRecords"
            :pageIndex="pageIndex"
            :isLoading="uiFlags.isIpFetching"
            :totalCount="ipMeta.totalCount || 0"
            @pageChange="onPageNumberChange"
            @deleteBlacklist="onDeleteBlacklist"
            @editBlacklist="onEditBlacklist"
          />
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>

<script setup>
import { ref, computed, onMounted, reactive } from 'vue';
import { useStore } from 'vuex';
import WootButton from 'dashboard/components/ui/WootButton.vue';
import SettingsLayout from '../SettingsLayout.vue';
import IPBlacklistTable from './components/IPBlacklistTable.vue';
import Modal from 'dashboard/components/Modal.vue';
import AddIpRange from './components/AddIpRange.vue';

const store = useStore();
// --- Vuex State and Getters ---
const ipRecords = computed(() => store.getters['blacklist/getIPBlacklist']);
const uiFlags = computed(() => store.getters['blacklist/getUIFlags']);
const ipMeta = computed(() => store.getters['blacklist/getIPMeta']);

// 分页索引，从 0 开始计数
const pageIndex = ref(0);

// 筛选条件
const filters = reactive({
  ip: '',
});

// --- 方法 ---
const fetchIPBlacklistRecords = () => {
  // 构建查询参数
  const params = {};
  params.size = 30; // 设置每页显示2条记录
  // 添加分页参数
  params.page = pageIndex.value + 1; // API从1开始计数
  
  // 添加 IP 搜索参数
  if (filters.ip) {
    params.ip = filters.ip;
  }
 
  // 调用API获取数据
  store.dispatch('blacklist/fetchIPBlacklistRecords', params);
};

// 确保国际化资源加载完成
const ensureI18nLoaded = async () => {
  // 通常不需要特殊处理，如果有需要可以在这里添加逻辑
  return true;
};

// 组件挂载时处理
onMounted(async () => {
  // 先确保国际化资源加载完成
  await ensureI18nLoaded();
  // 加载IP黑名单列表
  fetchIPBlacklistRecords();
});

const onPageNumberChange = (page) => {
  pageIndex.value = page;
  fetchIPBlacklistRecords();
};

// 处理删除黑名单事件
const onDeleteBlacklist = () => {
  // 删除成功后刷新列表
  fetchIPBlacklistRecords();
};

// 处理编辑黑名单事件
const onEditBlacklist = (blacklistData) => {
  // 设置编辑模式
  isEditMode.value = true;
  // 保存编辑的黑名单数据
  editingBlacklistData.value = blacklistData;
  // 打开编辑弹框
  showAddIpRangeModal.value = true;
  
  // 编辑完成后会通过 AddIpRange 组件的 @refresh 事件刷新列表
};

// 搜索按钮点击事件
const onSearch = () => {
  // 获取输入的 IP 地址
  filters.ip = ipSegments.value.filter(segment => segment).join('.');
  
  pageIndex.value = 0; // 重置为第一页
  fetchIPBlacklistRecords();
};

// 重置按钮点击事件
const onReset = () => {
  // 重置所有筛选条件
  Object.keys(filters).forEach(key => {
    filters[key] = '';
  });
  
  // 重置 IP 输入框
  ipSegments.value = ['', '', '', ''];
  
  pageIndex.value = 0; // 重置为第一页
  fetchIPBlacklistRecords();
};

// IP 地址输入
const ipSegments = ref(['', '', '', '']);

// IP段添加弹框
const showAddIpRangeModal = ref(false);
// 当前编辑的黑名单数据
const editingBlacklistData = ref(null);
// 是否处于编辑模式
const isEditMode = ref(false);

// 打开IP段添加弹框
const openAddIpRangeModal = () => {
  // 重置编辑状态
  isEditMode.value = false;
  editingBlacklistData.value = null;
  // 显示弹框
  showAddIpRangeModal.value = true;
};

const handleIpInput = (event, index) => {
  const value = event.target.value.replace(/\D/g, '');
  if (value && parseInt(value) > 255) {
    ipSegments.value[index] = '255';
  } else {
    ipSegments.value[index] = value;
  }
  
  // 自动跳转到下一个输入框
  if (value.length === 3 && index < 3) {
    const nextInput = event.target.closest('.ip-input-group').querySelector(`input[data-index="${index + 1}"]`);
    if (nextInput) nextInput.focus();
  }
};

const handleBackspace = (event, index) => {
  if (!event.target.value && index > 0 && event.key === 'Backspace') {
    const prevInput = event.target.closest('.ip-input-group').querySelector(`input[data-index="${index - 1}"]`);
    if (prevInput) {
      prevInput.focus();
      prevInput.setSelectionRange(prevInput.value.length, prevInput.value.length);
    }
  }
};

const handlePaste = (event) => {
  event.preventDefault();
  const pastedText = (event.clipboardData || window.clipboardData).getData('text');
  const ipParts = pastedText.split('.');
  
  if (ipParts.length === 4) {
    ipParts.forEach((part, index) => {
      const value = part.replace(/\D/g, '');
      ipSegments.value[index] = parseInt(value) > 255 ? '255' : value;
    });
  }
};
</script>

<style lang="scss" scoped>
.settings-content {
  min-height: calc(100vh - 4.5rem);
  width: 100%;
}

.filter-section {
  @apply mb-6;
}

</style>
